/**
 * Data Access Layer - Model Repository
 * Handles all data access operations for models
 * In a real application, this would interact with APIs or databases
 */

import api from '../interceptors/interceptor';
import { API_ROUTES } from '../constants/apiRoutes';
import { Model } from '../models/Model';
import { modelsData } from '../data/models';

export class ModelRepository {
  private static instance: ModelRepository;
  private models: Model[];

  private constructor() {
    // Initialize with data from the data layer
    this.models = [...modelsData.models];
  }

  /**
   * Singleton pattern - ensure only one instance exists
   */
  public static getInstance(): ModelRepository {
    if (!ModelRepository.instance) {
      ModelRepository.instance = new ModelRepository();
    }
    return ModelRepository.instance;
  }

  /**
   * Get all models
   */
  public async getAllModels(): Promise<Model[]> {
    return (await api.get(API_ROUTES.MODEL_CONFIGS))?.data;
  }

  /**
   * Get model by ID
   */
  public async getModelById(id: string): Promise<Model | null> {
    return (await api.get(API_ROUTES.MODEL_CONFIGS_ID(id)))?.data;
  }

  /**
   * Create new model
   */
  public async createModel(model: Omit<Model, 'id'>): Promise<Model> {
    return new Promise((resolve) => {
      setTimeout(() => {
        const newModel: Model = {
          ...model,
          id: Date.now().toString(),
        };
        this.models.push(newModel);
        resolve({ ...newModel });
      }, 100);
    });
  }

  /**
   * Update existing model
   */
  public async updateModel(id: string, updates: Partial<Model>): Promise<Model | null> {
    return new Promise((resolve) => {
      setTimeout(() => {
        const index = this.models.findIndex(m => m.id === id);
        if (index === -1) {
          resolve(null);
          return;
        }
        this.models[index] = { ...this.models[index], ...updates };
        resolve({ ...this.models[index] });
      }, 100);
    });
  }

  /**
   * Delete model
   */
  public async deleteModel(id: string): Promise<boolean> {
    return new Promise((resolve) => {
      setTimeout(() => {
        const index = this.models.findIndex(m => m.id === id);
        if (index === -1) {
          resolve(false);
          return;
        }
        this.models.splice(index, 1);
        resolve(true);
      }, 100);
    });
  }

  /**
   * Search models by query
   */
  public async searchModels(query: string): Promise<Model[]> {
    return new Promise((resolve) => {
      setTimeout(() => {
        const lowerQuery = query.toLowerCase();
        const filtered = this.models.filter(model =>
          model.model_name.toLowerCase().includes(lowerQuery) ||
          model.provider.toLowerCase().includes(lowerQuery) ||
          model.description.toLowerCase().includes(lowerQuery) ||
          model.version.toLowerCase().includes(lowerQuery)
        );
        resolve([...filtered]);
      }, 100);
    });
  }
}
